import React, { useState, useMemo, useEffect } from 'react';
import {
  Building2, RefreshCw, Search, Eye, Filter, Table, Grid3X3,
  FileText, Clock, AlertCircle, Plus, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight,
} from 'lucide-react';

import { useApiData } from './hooks/useApi';
import { LoadingSpinner } from './components/LoadingSpinner';
import { EditableCell } from './components/EditableCell';
import { FormView } from './components/FormView';
import { HistoryModal } from './components/HistoryModal';
import { AddIndustryModal } from './components/AddIndustryModal';
import { getDataTypeInfo } from './utils/colors';
import { GetPermissions } from '../../../../app/modules/auth';
import { PageTitle } from '../../../../_metronic/layout/core';

export const TimeGap: React.FC = () => {
  const permissions = GetPermissions();
  
  const { data, loading, error, refetch, updateColumnValue, updateGoLive, createIndustry } = useApiData();
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [editEnabled, setEditEnabled] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [showAllIndustries, setShowAllIndustries] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'form'>('table');
  const [showAddModal, setShowAddModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    type: 'global' | 'cell';
    columnId?: number;
    industryId?: number;
    columnName?: string;
    industryName?: string;
  }>({ isOpen: false, type: 'global' });

  useEffect(() => {
    setEditEnabled(permissions?.some(feature => feature.name.includes('datatransfer-time-gap-edit')));
    console.log(permissions, permissions?.some(feature => feature.name.includes('datatransfer-time-gap-edit')));
  }, [permissions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedIndustry, showAllIndustries, selectedSections]);

  const filteredIndustries = useMemo(() => {
  if (!data) return [];

  return Object.keys(data)
    .filter(industry =>
      industry.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
}, [data, searchTerm]);

  // Get available sections for selected industry or all industries
  const availableSections = useMemo(() => {
    if (!data || !selectedIndustry) return [];
    const industry = data[selectedIndustry];
    if (!industry) return [];
    return Object.keys(industry.sections);
  }, [data, selectedIndustry]);

  const allAvailableSections = useMemo(() => {
    if (!data) return [];
    const sectionsSet = new Set<string>();
    Object.values(data).forEach(industry => {
      Object.keys(industry.sections).forEach(sectionName => {
        sectionsSet.add(sectionName);
      });
    });
    return Array.from(sectionsSet);
  }, [data]);

  // Generate table data based on selections
  const tableData: any = useMemo(() => {
    if (!data) return { columns: [], rows: [], sectionsData: {} };

    const industries = showAllIndustries ? filteredIndustries : [selectedIndustry];
    const sectionsToShow = selectedSections.length > 0
      ? selectedSections
      : (showAllIndustries ? allAvailableSections : availableSections);

    const columnsMap = new Map<string, {
      name: string;
      columnId: number;
      type: string;
      subsection: string;
      section: string;
      sectionId?: number;
    }>();

    const sectionsData: Record<string, { sectionId: number }> = {};

    // Collect all unique columns from selected sections
    industries.forEach(industryName => {
      const industry = data[industryName];
      if (!industry) return;

      Object.entries(industry.sections).forEach(([sectionName, section]) => {
        if (!sectionsToShow.includes(sectionName)) return;

        sectionsData[sectionName] = { sectionId: section.sectionId };

        Object.entries(section.columns).forEach(([columnName, column]) => {
          const key = `${sectionName}-${columnName}`;
          if (!columnsMap.has(key)) {
            columnsMap.set(key, {
              name: columnName,
              columnId: column.columnId,
              type: column.type,
              subsection: column.subsection,
              section: sectionName,
              sectionId: section.sectionId,
            });
          }
        });
      });
    });

    const columns = Array.from(columnsMap.values());

    // Sort columns by subsection (natural sort for b, b1, b2, b3, c, c1, c2...)
    columns.sort((a, b) =>
      a.subsection.localeCompare(b.subsection, undefined, { numeric: true, sensitivity: 'base' })
    );

    // Generate rows
    const rows = industries.map(industryName => {
      const industry = data[industryName];
      if (!industry) return null;

      const row: Record<string, any> = {
        id: {
          value: industryName,
          valueId: industry.industryId,
        },
      };

      columns.forEach(column => {
        const section = industry.sections[column.section];
        const columnData = section?.columns[column.name];

        if (columnData) {
          row[column.name] = {
            value: columnData.value.value,
            valueId: columnData.value.id,
            columnId: columnData.columnId,
            type: columnData.type,
          };
        } else {
          row[column.name] = {
            value: '',
            valueId: 0,
            columnId: 0,
            type: column.type,
          };
        }
      });

      columns.forEach(col => {
        if (col.type === 'distributors_count') {
          const src = columns.find(c => c.section === col.section && c.type === 'distributors');
          const raw = src ? (row[src.name]?.value as string) : '';
          const count = raw ? raw.split(',').filter(v => v && v.trim() !== '').length : 0;
          row[col.name] = {
            ...(row[col.name] || {}),
            value: String(count),
            valueId: row[col.name]?.valueId || 0,
            columnId: row[col.name]?.columnId || 0,
            type: 'distributors_count',
          };
        }
      });

      return row;
    }).filter(Boolean);

    return { columns, rows, sectionsData };
  }, [data, selectedIndustry, showAllIndustries, selectedSections, filteredIndustries, availableSections, allAvailableSections]);

  // ── Paginated rows ───────────────────────────────────────────────────────────
  const totalRows = tableData.rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return tableData.rows.slice(start, start + rowsPerPage);
  }, [tableData.rows, currentPage, rowsPerPage]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSectionToggle = (sectionName: string) => {
    setSelectedSections(prev =>
      prev.includes(sectionName) ? prev.filter(s => s !== sectionName) : [...prev, sectionName]
    );
  };

  const resetFilters = () => {
    setSelectedIndustry('');
    setSelectedSections([]);
    setShowAllIndustries(true);
    setSearchTerm('');
  };

  const handleCellSave = async (valueId: number, columnId: number, newValue: string, industryId?: number): Promise<boolean> => {
    if (!valueId || !columnId) return false;
    const result = await updateColumnValue(columnId, industryId || valueId, newValue);
    return result.success;
  };

  const handleGoLiveSave = async (industryId: number, newValue: string): Promise<boolean> => {
    const result = await updateGoLive(industryId, newValue);
    return result.success;
  };

  /**
   * Salva o nome da indústria reutilizando o endpoint updateGoLive com um
   * terceiro parâmetro: updateGoLive(id, goLiveAtual, novoNome)
   */
  const handleNameSave = async (industryId: number, currentGoLive: string, newName: string): Promise<boolean> => {
    const result = await updateGoLive(industryId, currentGoLive, newName);
    return result.success;
  };

  const openGlobalHistory = () => {
    setHistoryModal({ isOpen: true, type: 'global' });
  };

  const openCellHistory = (columnId: number, industryId: number, columnName: string, industryName: string) => {
    setHistoryModal({ isOpen: true, type: 'cell', columnId, industryId, columnName, industryName });
  };

  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, type: 'global' });
  };

  const handleCreateIndustry = async (industryData: {
    name: string;
    numeroGoLive: string;
    fields: Record<string, string>;
  }): Promise<boolean> => {
    const result = await createIndustry(industryData);
    return result.success;
  };

  // ── Loading / Error states ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Carregando dados das indústrias...</p>
          <p className="text-gray-400 text-sm mt-1">Isso pode levar alguns instantes</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao Carregar Dados</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 w-full">
      {/* Header */}
      <PageTitle>Prospecção Indústria</PageTitle>
      <div className="bg-white shadow-sm border-b">
        <div className="w-full px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-gray-600">Selecione uma indústria para visualizar e editar os dados</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {editEnabled ? (
                <button
                  onClick={() => setShowAddModal(true)}
                  disabled={!editEnabled}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  title="Adicionar nova indústria"
                >
                  <Plus className="h-4 w-4" />
                  <span>Adicionar Indústria</span>
                </button>
              ) : null}

              <button
                onClick={openGlobalHistory}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Ver histórico de alterações"
              >
                <Clock className="h-4 w-4" />
                <span>Histórico</span>
              </button>

              <button
                onClick={refetch}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Atualizar dados"
              >
                <RefreshCw className="h-5 w-5" />
              </button>

              {(selectedIndustry || selectedSections.length > 0) && (
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar indústrias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Industry Selection */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Selecionar Indústria</h2>
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => {
                setShowAllIndustries(true);
                setSelectedIndustry('');
              }}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                showAllIndustries
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" />
                <span className="font-medium">Todas as Indústrias</span>
              </div>
            </button>
          </div>

          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredIndustries.map((industryName) => {
                const industryData = data![industryName];
                const isSelected = selectedIndustry === industryName && !showAllIndustries;

                return (
                  <button
                    key={industryName}
                    onClick={() => {
                      setSelectedIndustry(industryName);
                      setShowAllIndustries(false);
                      setSelectedSections([]);
                    }}
                    className={`p-6 rounded-xl border-2 transition-all hover:shadow-lg text-left ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="p-3 rounded-lg bg-blue-500">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{industryName}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Go Live: {industryData.numeroGoLive}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Section Filter */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Filtrar por Seção</h2>
              <p className="text-sm text-gray-600">
                {showAllIndustries
                  ? 'Selecione seções para filtrar dados de todas as indústrias'
                  : `Filtrando dados da indústria: ${selectedIndustry}`}
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Table className="h-4 w-4" />
                <span>Tabela</span>
              </button>
              <button
                onClick={() => setViewMode('form')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'form'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {showAllIndustries ? <Grid3X3 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                <span>{showAllIndustries ? 'Cards' : 'Formulário'}</span>
              </button>
            </div>
          </div>

          {(showAllIndustries ? allAvailableSections : availableSections).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Seções Disponíveis:</span>
                {selectedSections.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    {selectedSections.length} selecionada{selectedSections.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {(showAllIndustries ? allAvailableSections : availableSections).map((sectionName) => {
                  const isSelected = selectedSections.includes(sectionName);
                  return (
                    <button
                      key={sectionName}
                      onClick={() => handleSectionToggle(sectionName)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Eye className="h-3 w-3" />
                      <span className="font-medium">{sectionName}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Table / Form */}
        {tableData.columns.length > 0 && (
          <>
            {viewMode === 'table' ? (
              <div className="bg-white rounded-xl shadow-sm border">
                {/* Table header bar */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Table className="h-5 w-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {showAllIndustries ? 'Todas as Indústrias' : `Dados: ${selectedIndustry}`}
                      </h3>
                    </div>
                    <div className="text-sm text-gray-500">
                      {showAllIndustries ? `${totalRows} indústrias` : '1 indústria'} • {tableData.columns.length} colunas
                    </div>
                  </div>
                </div>

                {/* Scrollable table */}
                <div className="overflow-x-auto max-h-[calc(100vh-400px)] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0 z-9999 shadow-sm" style={{ zIndex: 9999 }}>
                      <tr>
                        {/* ── Nome (sticky left + editable) ── */}
                        <th className="
                          px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                          border-r border-gray-200
                          sticky left-0 top-0 bg-gray-50 z-20
                        ">
                          Nome
                        </th>

                        {/* ── Número Go Live ── */}
                        <th className="
                          px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                          border-r border-gray-200 top-0 bg-gray-50 z-20
                        ">
                          Número Go Live
                        </th>

                        {tableData.columns.map((column: any) => (
                          <th
                            key={`${column.section}-${column.name}`}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0"
                          >
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold">{column.name}</span>
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDataTypeInfo(column.type).bgColor} ${getDataTypeInfo(column.type).color}`}
                                  title={`Tipo: ${getDataTypeInfo(column.type).label}`}
                                >
                                  {getDataTypeInfo(column.type).icon}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 normal-case">
                                {column.section} • {column.subsection}
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedRows.map((row: any) => {
                        const industryKey = showAllIndustries ? row.id.value : selectedIndustry;
                        const currentGoLive = data![industryKey]?.numeroGoLive || '';

                        return (
                          <tr key={row.id.value} className="hover:bg-gray-50">
                            {/* ── Nome — editável ── */}
                            <td className="
                              px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900
                              border-r border-gray-200
                              sticky left-0 bg-white z-10
                            ">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                                  <Building2 className="h-4 w-4 text-white" />
                                </div>
                                <div className="ml-3">
                                  <EditableCell
                                    value={showAllIndustries ? row.id.value : selectedIndustry}
                                    type="texto"
                                    onSave={(newName) =>
                                      handleNameSave(row.id.valueId, currentGoLive, newName)
                                    }
                                    disabled={!editEnabled}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* ── Número Go Live ── */}
                            <td className="
                              px-6 py-4 whitespace-nowrap text-sm text-gray-700
                              border-r border-gray-200 bg-white z-10
                            ">
                              <EditableCell
                                value={currentGoLive}
                                type="texto"
                                onSave={(newValue) =>
                                  handleGoLiveSave(row.id.valueId, newValue)
                                }
                                disabled={!editEnabled}
                              />
                            </td>

                            {/* ── Data columns ── */}
                            {tableData.columns.map((column: any) => {
                              const cellData = row[column.name];
                              return (
                                <td
                                  key={`${row.id.value}-${column.name}`}
                                  className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200 last:border-r-0"
                                >
                                  <EditableCell
                                    value={cellData?.value || ''}
                                    type={cellData?.type as 'data' | 'número' | 'texto' | 'categoria_mercado' | 'yesno' | 'status_prospeccao' | 'status_industria' | 'percent' | 'distributors' | 'distributors_count'}
                                    onSave={(newValue) =>
                                      handleCellSave(cellData?.valueId || 0, cellData?.columnId || 0, newValue, row.id.valueId)
                                    }
                                    disabled={!cellData?.valueId || !editEnabled}
                                    onShowHistory={cellData?.valueId ? () => {
                                      openCellHistory(cellData.columnId, row.id.valueId, column.name, industryKey);
                                    } : undefined}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination controls ─────────────────────────────────────── */}
                {totalRows > 0 && (
                  <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Rows per page selector */}
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span>Linhas por página:</span>
                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {[10, 20, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>

                    {/* Page info + navigation */}
                    <div className="flex items-center space-x-1 text-sm text-gray-600">
                      <span className="mr-3">
                        {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalRows)} de {totalRows}
                      </span>

                      {/* First page */}
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Primeira página"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </button>

                      {/* Previous page */}
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Página anterior"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {/* Page number pills */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                          if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((item, idx) =>
                          item === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400 select-none">…</span>
                          ) : (
                            <button
                              key={item}
                              onClick={() => setCurrentPage(item as number)}
                              className={`min-w-[2rem] px-2 py-1 rounded text-sm font-medium transition-colors ${
                                currentPage === item
                                  ? 'bg-blue-600 text-white'
                                  : 'hover:bg-gray-100 text-gray-700'
                              }`}
                            >
                              {item}
                            </button>
                          )
                        )}

                      {/* Next page */}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Próxima página"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>

                      {/* Last page */}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Última página"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {tableData.rows.length === 0 && (
                  <div className="text-center py-12">
                    <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum dado disponível para os filtros selecionados</p>
                  </div>
                )}
              </div>
            ) : (
              <FormView
                data={tableData}
                selectedIndustry={showAllIndustries ? '' : selectedIndustry}
                onCellSave={handleCellSave}
                sectionsData={tableData.sectionsData}
                onShowCellHistory={openCellHistory}
                editEnabled={editEnabled}
              />
            )}
          </>
        )}

        {/* Empty State */}
        {tableData.columns.length === 0 && (
          <div className="text-center py-12">
            <Filter className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum Dado Encontrado</h3>
            <p className="text-gray-500">
              {selectedSections.length > 0
                ? 'Nenhum dado disponível para as seções selecionadas'
                : 'Selecione pelo menos uma seção para visualizar os dados'}
            </p>
          </div>
        )}

        {/* History Modal */}
        <HistoryModal
          isOpen={historyModal.isOpen}
          onClose={closeHistoryModal}
          title={historyModal.type === 'global' ? 'Histórico Geral de Alterações' : 'Histórico da Célula'}
          type={historyModal.type}
          columnId={historyModal.columnId}
          industryId={historyModal.industryId}
          columnName={historyModal.columnName}
          industryName={historyModal.industryName}
          data={data ?? undefined}
        />

        {/* Add Industry Modal */}
        <AddIndustryModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleCreateIndustry}
          data={data ?? undefined}
        />
      </div>
    </div>
  );
};

export default TimeGap;
